import { SendIcon } from 'lucide-react'
import type { FC, ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

export interface ChatInputSendButtonProps {
  canSend: boolean
  isLoading: boolean
  isNoteMode: boolean
  onClick: () => void
}

/**
 * Send button for the chat input toolbar.
 */
export const ChatInputSendButton: FC<ChatInputSendButtonProps> = ({
  canSend,
  isLoading,
  isNoteMode,
  onClick,
}): ReactNode => {
  const variant = canSend ? (isNoteMode ? 'secondary' : 'default') : 'ghost'
  const tooltipText = isNoteMode ? 'Send note (Enter)' : 'Send message (Enter)'
  const ariaLabel = isNoteMode ? 'Send note' : 'Send message'

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={variant}
          disabled={!canSend}
          onClick={onClick}
          className={cn(
            'chat-send-button gap-1.5 transition-all',
            canSend ? 'scale-100' : 'scale-95 opacity-50',
            isNoteMode &&
              canSend &&
              'bg-yellow-500 text-white hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700'
          )}
          aria-label={ariaLabel}
        >
          {isLoading ? (
            <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <SendIcon className="size-4" />
          )}
          <span className="hidden sm:inline">Send</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">{tooltipText}</TooltipContent>
    </Tooltip>
  )
}

ChatInputSendButton.displayName = 'ChatInputSendButton'
